import React, { useState } from "react";
import { Table, Button, Card, Flex, Typography, Tag, notification, Dropdown as AntDropdown, Tabs } from "antd";
import VideoPreview from "components/UI/Video/VideoPreview";
import { useParams } from "react-router-dom";
import { useGetDomainGymQuery, useGetGymWorkoutsQuery, useGetScheduledStreamsQuery } from "store/api/api";
import { updateSelectedMuxVideoUrl, updateSelectedWorkout } from "store/slices";
import Categories from "components/UI/Categories";
import "./styles.scss";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import LiveStreamIcon from "images/svgIcons/live-stream";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import PencilIcon from "images/svgIcons/pencil";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CopyOutlined, PlusOutlined, DownOutlined } from "@ant-design/icons";
import NeedHelpComponent from "./NeedHelp";
import { ScheduleLiveModal, PreRecordedLive } from "components/Modals/ScheduleLiveModal";
import { EditWorkoutModal } from "components/Modals/EditWorkoutModal";
import { useDispatch } from "react-redux";
import GoLive from "./GoLive";
import { workoutTypes } from "helpers/constants";
import StartingSoon from "./StartingSoon";

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const TabLabels = ({ children, active }) => (
	<Typography.Text style={{ color: active ? "#000" : "rgba(0,0,0,0.5)" }} className="tab-label" strong>
		{children}
	</Typography.Text>
);

const LiveStreams = () => {
	const { gym } = useParams();
	const dispatch = useDispatch();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse?.data?.id;
	const workoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });
	const [activeTab, setActiveTab] = useState("scheduledLiveStreams");
	const {
		data: scheduledStreams,
		refetch: refetchStreams,
		isFetching,
	} = useGetScheduledStreamsQuery(domainGymId, {
		skip: !domainGymResponse.data,
	});
	const [, updateIsShowingVideoPlayer] = React.useState(false);
	const [, updateFileSelected] = React.useState("");
	const primaryColor = usePrimaryColor();
	const [showLiveScheduleModal, setShowLiveScheduleModal] = React.useState(false);
	const [showEditWorkoutModal, setShowEditWorkoutModal] = useState(false);
	const [selectedLiveStream, setSelectedLiveStream] = useState(null);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [showPreRecordedLiveModal, setShowPreRecordedLiveModal] = useState(false);

	const liveWorkouts = workoutsResponse.data?.workouts?.filter((workout) => !!workout.start_time) || [];
	const streamsToShow = scheduledStreams
		?.map((stream) => {
			const match = liveWorkouts.find((workout) => workout.id === stream.workout_id);
			return match && { ...stream, ...match };
		})
		.filter(Boolean)
		.sort((a, b) => {
			const isAfter = dayjs.tz(a.start_time, a.time_zone).isAfter(dayjs.tz(b.start_time, b.time_zone));
			if (isAfter) return 1;
			else return -1;
		});

	const preRecorded = streamsToShow?.filter((stream) => stream.type === workoutTypes.SCHEDULED_PRE_RECORDED);
	const scheduled = streamsToShow?.filter((stream) => stream.type !== workoutTypes.SCHEDULED_PRE_RECORDED);
	const onEditSetVisible = (bool, workout) => {
		if (workout) {
			dispatch(updateSelectedWorkout({ workout }));
			setSelectedLiveStream(workout);
		}
		setShowEditWorkoutModal(bool);
		if (!bool) {
			dispatch(updateSelectedWorkout({ workout: null }));
			dispatch(updateSelectedMuxVideoUrl({ url: null }));
		}
	};

	const openGoLiveModal = (workout) => {
		if (workout.currently_streaming) return;
		dispatch(updateSelectedWorkout({ workout }));
		setIsModalVisible(true);
	};

	const columns = [
		{
			title: "Live Stream",
			dataIndex: "livestream",
			key: "livestream",
			render: (text, record) => (
				<Flex wrap={false} style={{ minWidth: 300 }} align="center" data-id="liveVideo">
					<VideoPreview
						updateIsShowingVideoPlayer={updateIsShowingVideoPlayer}
						updateFileSelected={updateFileSelected}
						record={record}
					/>
				</Flex>
			),
		},
		{
			title: "Scheduled Date and Time",
			dataIndex: "start_time",
			key: "start_time",
			render: (text, record) => (
				<Flex gap={8} align="center">
					<LiveStreamIcon />
					{text && (
						<Typography.Paragraph style={{ fontSize: 10 }} data-id="lblScheduledDateTime">
							{dayjs.tz(text, record.time_zone).format("YYYY/MM/DD | hh:mm A | z")}
						</Typography.Paragraph>
					)}
				</Flex>
			),
		},
		{
			title: "Categories",
			dataIndex: "categories",
			key: "categories",
			render: (row) =>
				row ? (
					<Categories style={{ flexWrap: "nowrap" }} maxVisibleCategories={1} values={row.map((r) => r.name)} />
				) : null,
		},
		{
			title: "Music",
			dataIndex: "music_genre",
			key: "music",
			render: (text, record) => {
				return (
					text && (
						<Tag style={{ height: 24, padding: "4px 8px" }} className="music-tag">
							{record.music_genre}
						</Tag>
					)
				);
			},
		},
		...(activeTab === "scheduledLiveStreams"
			? [
					{
						title: "Livestream Key",
						dataIndex: "mux_stream_key",
						key: "livestreamKey",
						width: 200,
						render: (text, record) => {
							return (
								<Typography.Paragraph
									ellipsis
									copyable={{
										text,
										icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
										onCopy: () => notification.success({ message: "Copied to clipboard" }),
									}}
									style={{ maxWidth: 110 }}
									data-id="sectionLiveStreamKey"
								>
									{record.mux_stream_key?.substring(0, 12)}
								</Typography.Paragraph>
							);
						},
					},
			  ]
			: []),
		{
			title: "Duration",
			dataIndex: "duration",
			key: "duration",
			render: (text) => {
				const duration = dayjs.duration(text, "seconds");
				return duration.format("HH:mm:ss");
			},
		},
		{
			title: "Actions",
			key: "actions",
			fixed: "right",
			render: (text, record) => (
				<Flex justify="space-between" align="center">
					{record.type !== workoutTypes.SCHEDULED_PRE_RECORDED && (
						<Button
							onClick={() => openGoLiveModal(record)}
							size="small"
							className="primary-filled need-help-btn"
							style={{ marginRight: 8, backgroundColor: primaryColor, flex: 1, minWidth: 100 }}
							data-id="btnOpenLive"
						>
							<Typography.Text style={{ color: "white" }}>
								<LiveStreamIcon />
								<span className="action-text">{record?.currently_streaming ? "Live" : "Go Live"}</span>
							</Typography.Text>
						</Button>
					)}
					<Button
						onClick={() => onEditSetVisible(true, record)}
						className="need-help-btn"
						variant="outlined"
						size="small"
						style={{ flex: 1 }}
						disabled={record?.type === workoutTypes.CURRENTLY_LIVE}
						data-id="btnLiveEdit"
					>
						<Typography.Text style={{ color: primaryColor }}>
							<PencilIcon />
							<span className="action-text">Edit</span>
						</Typography.Text>
					</Button>
				</Flex>
			),
		},
	];

	return (
		<Flex vertical className="livestreams">
			<Flex style={{ marginBottom: 16 }} align="center" gap={16}>
				<Typography.Title style={{ margin: 0 }} level={3}>
					Starting Soon
				</Typography.Title>
			</Flex>
			<StartingSoon />
			<Flex align="center" justify="space-between" gap={16} wrap className="livestreams-header">
				<Flex gap={16} align="center">
					<Typography.Title className="page-title" style={{ margin: 0 }} level={3}>
						All Live Streams
					</Typography.Title>
					<NeedHelpComponent />
				</Flex>
				<Flex className="flex-btnNewScheduleLive">
					<AntDropdown
						style={{ flex: 1, width: "100%" }}
						trigger={["click"]}
						menu={{
							items: [
								{
									label: "Schedule Live",
									key: "createLiveWorkout",
									onClick: () => {
										setShowLiveScheduleModal(true);
									},
								},
								{
									label: "Pre-recorded Live",
									key: "createOnDemandWorkout",
									onClick: () => {
										setShowPreRecordedLiveModal(true);
									},
								},
							],
						}}
					>
						<Button data-id="btnNewScheduleLive" style={{ flex: 1, width: "100%" }} type="primary">
							<PlusOutlined /> New <DownOutlined />
						</Button>
					</AntDropdown>
					<PreRecordedLive open={showPreRecordedLiveModal} setOpen={setShowPreRecordedLiveModal} />
					<ScheduleLiveModal open={showLiveScheduleModal} setOpen={setShowLiveScheduleModal} />
				</Flex>
			</Flex>
			<Card>
				<Tabs
					onChange={setActiveTab}
					items={[
						{
							label: (
								<Flex gap={8} align="center">
									<TabLabels active={false}>Scheduled</TabLabels>
									<span className="data-count">{scheduled?.length}</span>
								</Flex>
							),
							key: "scheduledLiveStreams",
							children: (
								<Table
									loading={isFetching || workoutsResponse.isLoading}
									scroll={{ x: "auto" }}
									columns={columns}
									dataSource={scheduled}
									pagination={{
										showSizeChanger: true,
									}}
									data-id="tableLiveStreams"
								/>
							),
						},
						{
							label: (
								<Flex gap={8} align="center">
									<TabLabels active={false}>Pre recorded</TabLabels>
									<span className="data-count">{preRecorded?.length}</span>
								</Flex>
							),
							key: "preRecordedLiveStreams",
							children: (
								<Table
									loading={isFetching || workoutsResponse.isLoading}
									scroll={{ x: "auto" }}
									columns={columns}
									dataSource={preRecorded}
									pagination={{
										showSizeChanger: true,
									}}
									data-id="tablePreRecordedLiveStreams"
								/>
							),
						},
					]}
					data-id="liveStreamsTabs"
				/>
			</Card>

			<EditWorkoutModal
				open={showEditWorkoutModal}
				setOpen={onEditSetVisible}
				isEditingLive
				liveStream={selectedLiveStream}
				onClose={() => workoutsResponse?.refetch?.()}
			/>
			<GoLive open={isModalVisible} closeModal={() => setIsModalVisible(false)} refetchStreams={refetchStreams} />
		</Flex>
	);
};

export default LiveStreams;

